import type {
  IAssistantExcludedRow,
  IAssistantRow,
  IAssistantSummary,
  IAvailabilityValue,
  ICopyValue,
  ICreateValue,
  IFlagValue,
  IGroupLinksValue,
  IGroupRulesValue,
  IMoneyValue,
  INewCategoryValue,
  INewChoiceValue,
  INewGroupValue,
  INewStockItemValue,
  IOrderValue,
  IPlacementValue,
  IPriceValue,
  IRecipeValue,
  IRemovedValue,
  IScheduleValue,
  ISizesValue,
  IStockMoveValue,
  IStockQuantityValue,
  ITextValue,
  TAssistantValue,
  TRowField,
} from '@/interfaces/assistant.interface';

/**
 * The v1 value shapes. Every later shape either reuses one of them with the
 * same meaning or uses none of their keys (plan F3 forward-compat rule), so
 * these guards never misread a value of a later phase.
 */
export function isCreateValue(value: TAssistantValue): value is ICreateValue {
  return 'name' in value && 'categoryId' in value;
}

export function isPriceValue(value: TAssistantValue): value is IPriceValue {
  return 'price' in value && !isCreateValue(value);
}

export function isAvailabilityValue(
  value: TAssistantValue
): value is IAvailabilityValue {
  return 'available' in value;
}

export function isTextValue(value: TAssistantValue): value is ITextValue {
  return 'text' in value;
}

/*
 * The Phase 2 value shapes: each one has a key no other shape of the
 * contract uses (`menuId`, `on`, `categoryName`, `ids`), so these guards
 * never misread a value of another phase either.
 */

export function isNewCategoryValue(
  value: TAssistantValue
): value is INewCategoryValue {
  return 'menuId' in value && 'name' in value;
}

export function isFlagValue(value: TAssistantValue): value is IFlagValue {
  return 'on' in value;
}

export function isPlacementValue(
  value: TAssistantValue
): value is IPlacementValue {
  return 'categoryName' in value && 'categoryKey' in value;
}

export function isOrderValue(value: TAssistantValue): value is IOrderValue {
  return 'ids' in value && 'names' in value;
}

/*
 * The Phase 3 value shapes: each one has a key no other shape of the
 * contract uses at its top level (`groups`, `multiple`, `rules`, `active`,
 * `removed`), so these guards never misread a value of another phase
 * either. A new choice's shape is also a new size's (plan F3).
 */

export function isGroupLinksValue(
  value: TAssistantValue
): value is IGroupLinksValue {
  return 'groups' in value;
}

export function isGroupRulesValue(
  value: TAssistantValue
): value is IGroupRulesValue {
  return 'multiple' in value && 'required' in value;
}

export function isNewGroupValue(
  value: TAssistantValue
): value is INewGroupValue {
  return 'rules' in value && 'choices' in value;
}

export function isNewChoiceValue(
  value: TAssistantValue
): value is INewChoiceValue {
  return 'active' in value && 'price' in value;
}

export function isRemovedValue(value: TAssistantValue): value is IRemovedValue {
  return 'removed' in value;
}

/*
 * The Phase 4 value shapes: each one has a key no other shape of the
 * contract uses at its top level (`sizes`, `hourRanges`, `sourceItemId`), so
 * these guards never misread a value of another phase either. A new size is
 * an `INewChoiceValue` (plan F3).
 */

export function isSizesValue(value: TAssistantValue): value is ISizesValue {
  return 'sizes' in value;
}

export function isScheduleValue(
  value: TAssistantValue
): value is IScheduleValue {
  return 'hourRanges' in value;
}

export function isCopyValue(value: TAssistantValue): value is ICopyValue {
  return 'sourceItemId' in value;
}

/*
 * The Phase 5 value shapes: each one has a key no other shape of the
 * contract uses at its top level (`delta`, `amount`, `minimumQuantity`,
 * `lines`), except a quantity, whose `quantity` and `unit` a new stock
 * item also has (with its `minimumQuantity`), so these guards never misread a
 * value of another phase either.
 */

export function isStockMoveValue(
  value: TAssistantValue
): value is IStockMoveValue {
  return 'delta' in value && 'movement' in value;
}

export function isStockQuantityValue(
  value: TAssistantValue
): value is IStockQuantityValue {
  return (
    'quantity' in value && 'unit' in value && !('minimumQuantity' in value)
  );
}

export function isMoneyValue(value: TAssistantValue): value is IMoneyValue {
  return 'amount' in value;
}

export function isNewStockItemValue(
  value: TAssistantValue
): value is INewStockItemValue {
  return 'minimumQuantity' in value && 'unit' in value;
}

export function isRecipeValue(value: TAssistantValue): value is IRecipeValue {
  return 'lines' in value;
}

/**
 * Row-key families of products (plan F6): a product or its sizes, a new
 * product, a new size of a product, a copy of a product (plan P4).
 */
const PRODUCT_KEY_FAMILIES: readonly string[] = [
  'item',
  'new',
  'newSize',
  'copy',
];

/**
 * A row known only by its key (a kept or a changed one) is a product's
 * (`item:…`, `new:…`, plan F6); any other key (a category's, a menu's order)
 * makes the cards speak of changes (plan P2-4).
 */
export function isProductKey(key: string): boolean {
  return PRODUCT_KEY_FAMILIES.includes(key.split(':')[0]);
}

/** Row-key families of stock items (plan P5-1): an existing one's fields, a new one. */
const STOCK_ITEM_KEY_FAMILIES: readonly string[] = ['stock', 'newStock'];

/**
 * A row known only by its key is a stock item's (`stock:…`, `newStock:…`,
 * plan P5-1): gone, it left the inventory, not the menu (a recipe's key
 * names its product, size or choice, which are the menu's).
 */
export function isStockItemKey(key: string): boolean {
  return STOCK_ITEM_KEY_FAMILIES.includes(key.split(':')[0]);
}

/** A category left out (its key, plan F6): its copy is feminine (plan P2-4, X6). */
export function isCategoryExclusion(
  row: Pick<IAssistantExcludedRow, 'key'>
): boolean {
  return row.key?.startsWith('category:') ?? false;
}

/** Key families of new option choices (plan F6, P3-2): a new group's, or one added to a group. */
const NEW_CHOICE_KEY_FAMILIES: readonly string[] = ['newGroup', 'newChoice'];

/**
 * A new option choice left out (its key, plan P3-2): one of a group this
 * proposal creates (`newGroup:{n}`, the choice in `sizeName`) or one added to
 * a group (`newChoice:{groupId}:{n}`).
 */
export function isNewChoiceExclusion(
  row: Pick<IAssistantExcludedRow, 'key'>
): boolean {
  return (
    row.key !== undefined &&
    NEW_CHOICE_KEY_FAMILIES.includes(row.key.split(':')[0])
  );
}

/**
 * A new size of a product left out (its key, plan F6 `newSize:{itemId}:{n}`,
 * P4-1): its own discounted price is not below its price.
 */
export function isNewSizeExclusion(
  row: Pick<IAssistantExcludedRow, 'key'>
): boolean {
  return row.key?.startsWith('newSize:') ?? false;
}

/** A new product left out (its key, plan F6 `new:{n}`): it is not created. */
export function isNewProductExclusion(
  row: Pick<IAssistantExcludedRow, 'key'>
): boolean {
  return row.key?.startsWith('new:') ?? false;
}

/** «Coca-Cola 500 ml», or «Pizza · Grande» for a size (a row or a left-out one). */
export function rowItemLabel(
  row: Pick<IAssistantRow, 'itemName' | 'sizeName'>
): string {
  return row.sizeName ? `${row.itemName} · ${row.sizeName}` : row.itemName;
}

/**
 * The product a row changes; a row without one (a new product, a category,
 * an option group) is its own. A product id (a number) never equals a key.
 */
function productKey(row: IAssistantRow): number | string {
  return row.itemId ?? row.key;
}

/** How many products the rows change (the sizes of one product count once). */
export function productCount(rows: IAssistantRow[]): number {
  return new Set(rows.map(productKey)).size;
}

/** The first row, when every row changes the same product; null otherwise. */
export function singleProductRow(rows: IAssistantRow[]): IAssistantRow | null {
  return rows.length && productCount(rows) === 1 ? rows[0] : null;
}

/**
 * Whether the rows add products (a new product or a copy): an undone change
 * then says they were removed (plan P0-10).
 */
export function addsProducts(rows: IAssistantRow[]): boolean {
  return rows.some((row) => row.field === 'create' || row.field === 'copy');
}

/**
 * Rows that add something other than a product: a category (plan P2-1), an
 * option group or a choice (P3-1), a variant of a product that has variants
 * (P4-1), a stock item (P5-1). Their undo removes them (P2-4, P3-3, P4-1,
 * P5-2).
 */
const OTHER_CREATION_FIELDS: readonly TRowField[] = [
  'newCategory',
  'newGroup',
  'newChoice',
  'newSize',
  'newStockItem',
];

/**
 * Whether the rows add something other than a product (a category, an
 * option group, a choice, a variant or a stock item): an undone change then
 * says that what was added was removed, not products.
 */
export function addsOtherThanProducts(rows: IAssistantRow[]): boolean {
  return rows.some((row) => OTHER_CREATION_FIELDS.includes(row.field));
}

/**
 * Summaries of changes made only of ops that add something other than a
 * product (plan P2-1, P3-1, P5-1): each of their rows is a creation or needs
 * one (`requires`), so whatever part was applied added something.
 */
const OTHER_CREATION_CODES: readonly IAssistantSummary['code'][] = [
  'createCategory',
  'createOptionGroup',
  'addOptionChoices',
  'createStockItem',
];

/**
 * Whether a change added something other than a product, by its summary:
 * for when its rows are not at hand (a receipt that did not read them, the
 * first rows an activity shows).
 */
export function summaryAddsOtherThanProducts(
  summary: Pick<IAssistantSummary, 'code'>
): boolean {
  return OTHER_CREATION_CODES.includes(summary.code);
}
